import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, throwError, lastValueFrom } from 'rxjs';
import { DISABLE_AUTH_INTERCEPTOR } from '../../http-interceptors/auth-interceptor';
import { Document } from '../../models/document';
import { DocumentResponse, normalizeDocumentResponse } from '../../models/documentResponse';
import { AutoclassifierSdkService } from '../autoclassifier-sdk/autoclassifier-sdk.service';
import { AuthService } from '../auth/auth.service';
import {
  DocumentContentMetadataDto,
  DocumentContentMetadataCreateDto,
  DocumentContentMetadataUpdateDto
} from '@softdocs/capture-ui-libraries';

export interface DocumentLoadOptions {
  loadContent?: boolean;
  loadExtractionData?: boolean;
  batchDocuments?: Array<{ documentId: string; status: string }>;
}

export interface MultiDocumentLoadOptions extends DocumentLoadOptions {
  sequential?: boolean;
}

export interface KeyFieldsFormData {
  area: string;
  documentTypeCode: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentManagementService {
  private http = inject(HttpClient);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  private authService = inject(AuthService);

  /**
   * Fetches a document's content by its content URI
   * @param contentUri - The content URI of the document
   * @returns An observable with the document content as an ArrayBuffer
   */
  getDocument(contentUri: string): Observable<any> {
    const context = new HttpContext().set(DISABLE_AUTH_INTERCEPTOR, true);

    return this.http
      .get(contentUri, {
        responseType: 'arraybuffer',
        context
      })
      .pipe(
        catchError(error => {
          console.error('Error fetching document:', error.message);
          return throwError(() => new Error('Error fetching document'));
        })
      );
  }

  /**
   * Load a complete document with metadata and content
   * @param tenantId - Tenant identifier
   * @param documentId - Document identifier
   * @param options - Optional loading configuration
   * @returns Promise<Document> - Fully loaded document
   */
  async loadDocument(
    tenantId: string,
    documentId: string,
    options?: DocumentLoadOptions
  ): Promise<Document> {
    const authToken = this.authService.getAuthToken()!;
    const { loadContent = true, loadExtractionData = false, batchDocuments } = options || {};

    try {
      // 1. Get metadata from SDK
      const response: DocumentResponse =
        await this.autoclassifierSdkService.documentManagementSdk.getDocument(
          tenantId,
          documentId,
          undefined,
          authToken
        );

      const normalizedResponse = normalizeDocumentResponse(response);

      // 2. Create Document object
      const document: Document = {
        contentUri: normalizedResponse.contentUri,
        fileName: normalizedResponse.fileName,
        fileId: normalizedResponse.id,
        contentType: normalizedResponse.type,
        fileContent: '',
        completed:
          batchDocuments?.some(
            doc => doc.documentId === normalizedResponse.id && doc.status === 'filed'
          ) || false,
        classificationStarted: true,
        ecmDocId: normalizedResponse.ecmDocId,
        extractionRecordId: normalizedResponse.extractionRecordId,
        extractionStatus: normalizedResponse.extractionStatus as any
      };

      // 3. Load content if requested
      if (loadContent) {
        const arrayBuffer = await lastValueFrom(this.getDocument(document.contentUri));
        const binaryString = Array.from(new Uint8Array(arrayBuffer))
          .map(byte => String.fromCharCode(byte))
          .join('');
        document.fileContent = btoa(binaryString);
      }

      // 4. Load extraction data if requested and available
      if (
        loadExtractionData &&
        document.extractionStatus === 'extracted' &&
        document.extractionRecordId
      ) {
        const extractionData =
          await this.autoclassifierSdkService.documentAnalysisSdk.getExtractionStatus(
            document.extractionRecordId,
            tenantId,
            authToken
          );

        document.extractionData = extractionData;
        document.extractionStatus = extractionData.record_status;

        if (extractionData.record_status === 'extracted') {
          document.status = {
            documents: [
              {
                documentType:
                  extractionData.ai_doctype || extractionData.tenant_doctype || 'Unknown',
                confidence: extractionData.classification_result?.confidence || 1.0,
                pages: []
              }
            ]
          } as any;
        }
      }

      return document;
    } catch (error) {
      console.error(`Error loading document ${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Load multiple documents
   * @param tenantId - Tenant identifier
   * @param documentIds - Array of document identifiers
   * @param options - Optional loading configuration
   * @returns Promise<Document[]> - Array of loaded documents
   */
  async loadDocuments(
    tenantId: string,
    documentIds: string[],
    options?: MultiDocumentLoadOptions
  ): Promise<Document[]> {
    if (!documentIds || documentIds.length === 0) {
      return [];
    }

    const { sequential = false, ...loadOptions } = options || {};

    try {
      if (sequential) {
        const documents: Document[] = [];
        for (const docId of documentIds) {
          const doc = await this.loadDocument(tenantId, docId, loadOptions);
          documents.push(doc);
        }
        return documents;
      } else {
        return Promise.all(
          documentIds.map(docId => this.loadDocument(tenantId, docId, loadOptions))
        );
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      throw error;
    }
  }

  /**
   * Update document content (for split/merge scenarios)
   * Fetches the latest content for a document without reloading metadata
   * @param tenantId - Tenant identifier
   * @param documentId - Document identifier
   * @returns Promise<string> - Updated base64 content
   */
  async refreshDocumentContent(tenantId: string, documentId: string): Promise<string> {
    const authToken = this.authService.getAuthToken()!;

    try {
      const response: DocumentResponse =
        await this.autoclassifierSdkService.documentManagementSdk.getDocument(
          tenantId,
          documentId,
          undefined,
          authToken
        );

      const normalizedResponse = normalizeDocumentResponse(response);

      const arrayBuffer = await lastValueFrom(this.getDocument(normalizedResponse.contentUri));

      const binaryString = Array.from(new Uint8Array(arrayBuffer))
        .map(byte => String.fromCharCode(byte))
        .join('');

      return btoa(binaryString);
    } catch (error) {
      console.error(`Error refreshing document content for ${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Get document content metadata by document ID
   * Uses getDocument with include=contentmetadata first to check existence,
   * avoiding 404 errors for new documents without metadata.
   * @param tenantId - Tenant identifier
   * @param documentId - Document identifier
   * @returns Promise<DocumentContentMetadataDto | null>
   */
  async getDocumentContentMetadata(
    tenantId: string,
    documentId: string
  ): Promise<DocumentContentMetadataDto | null> {
    const accessToken = this.authService.getAuthToken()!;
    try {
      const result =
        await this.autoclassifierSdkService.documentManagementSdk.getDocumentContentMetadata(
          tenantId,
          documentId,
          accessToken
        );
      return result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Save key fields form data to document content metadata
   * Creates new metadata if it doesn't exist, updates if it does
   * @param tenantId - Tenant identifier
   * @param documentId - Document identifier
   * @param formData - Key fields form data including area, documentType, and key fields
   * @returns Promise<DocumentContentMetadataDto>
   */
  async saveKeyFieldsMetadata(
    tenantId: string,
    documentId: string,
    formData: KeyFieldsFormData
  ): Promise<DocumentContentMetadataDto> {
    const accessToken = this.authService.getAuthToken()!;

    const existing = await this.getDocumentContentMetadata(tenantId, documentId);

    const { area, documentTypeCode, ...keyFields } = formData;

    const metadataPayload = {
      area,
      documentTypeCode,
      keyFields
    };

    const dataString = JSON.stringify(metadataPayload);

    if (existing) {
      const updateDto: DocumentContentMetadataUpdateDto = {
        documentId: documentId,
        documentType: formData.documentTypeCode,
        data: dataString,
        updatedAt: new Date().toISOString()
      };

      await this.autoclassifierSdkService.documentManagementSdk.updateDocumentContentMetadata(
        tenantId,
        existing.documentContentMetadataId,
        updateDto,
        accessToken
      );

      return (await this.getDocumentContentMetadata(tenantId, documentId))!;
    } else {
      const createDto: DocumentContentMetadataCreateDto = {
        documentId: documentId,
        tenantId: tenantId,
        documentType: formData.documentTypeCode,
        data: dataString
      };

      const result =
        await this.autoclassifierSdkService.documentManagementSdk.createDocumentContentMetadata(
          tenantId,
          createDto,
          accessToken
        );
      return result;
    }
  }

  /**
   *
   * @param tenantId
   * @param documentId
   */
  async clearDocumentContentMetadata(tenantId: string, documentId: string): Promise<void> {
    const accessToken = this.authService.getAuthToken()!;
    const existing = await this.getDocumentContentMetadata(tenantId, documentId);
    if (!existing) return;

    const updateDto: DocumentContentMetadataUpdateDto = {
      documentId,
      documentType: '',
      data: JSON.stringify({}),
      updatedAt: new Date().toISOString()
    };

    const result =
      await this.autoclassifierSdkService.documentManagementSdk.updateDocumentContentMetadata(
        tenantId,
        existing.documentContentMetadataId,
        updateDto,
        accessToken
      );
  }

  /**
   * Parse stored metadata back into form data
   * @param metadata - Document content metadata
   * @returns KeyFieldsFormData | null
   */
  parseMetadataToFormData(metadata: DocumentContentMetadataDto | null): KeyFieldsFormData | null {
    if (!metadata || !metadata.data) {
      return null;
    }

    try {
      const parsed = JSON.parse(metadata.data);
      return {
        area: parsed.area,
        documentTypeCode: parsed.documentTypeCode,
        ...parsed.keyFields
      };
    } catch (error) {
      console.error('Error parsing document content metadata:', error);
      return null;
    }
  }
}
